import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader2, Headset, CheckCircle2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { sendSupportChat } from '../lib/api';

const IS_NATIVE = Capacitor.isNativePlatform();

const GREETING = "Hi 👋 I can help you with anything in GuestCheckout. What do you need a hand with?";

const SUGGESTIONS = [
  'How does the cleaning workflow work?',
  'How do I add a cleaning company?',
  'Where do I see performance?',
  'Who gets checkout emails?',
];

const genSessionId = () =>
  (window.crypto?.randomUUID?.() || `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`);

export default function SupportChat({ open: controlledOpen, onOpenChange } = {}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = (v) => { if (onOpenChange) onOpenChange(v); else setInternalOpen(v); };
  const [sessionId] = useState(genSessionId);
  const [messages, setMessages] = useState([{ role: 'assistant', content: GREETING }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [focused, setFocused] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) setFocused(false);
  }, [open]);

  // Native keyboard handling: take manual control so WKWebView does NOT drag the
  // fixed chat panel off-screen / detach the caret. We disable native resize while
  // the chat is open and track the real keyboard height into a CSS variable.
  useEffect(() => {
    if (!IS_NATIVE || !open) return;
    const root = document.documentElement;
    let showSub, hideSub;
    (async () => {
      try { await Keyboard.setResizeMode({ mode: KeyboardResize.None }); } catch (e) { /* noop */ }
      showSub = await Keyboard.addListener('keyboardWillShow', (info) => {
        root.style.setProperty('--kb-height', `${info.keyboardHeight}px`);
      });
      hideSub = await Keyboard.addListener('keyboardWillHide', () => {
        root.style.setProperty('--kb-height', '0px');
      });
    })();
    return () => {
      root.style.setProperty('--kb-height', '0px');
      if (showSub) showSub.remove();
      if (hideSub) hideSub.remove();
      Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(() => {});
    };
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, open]);

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setSending(true);
    try {
      const res = await sendSupportChat(sessionId, text);
      setMessages((m) => [...m, { role: 'assistant', content: res.data.reply, ticket: res.data.ticket_created }]);
    } catch (err) {
      setMessages((m) => [...m, {
        role: 'assistant',
        content: "Sorry, I couldn't reach the assistant just now. Please try again in a moment.",
        error: true,
      }]);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const showSuggestions = messages.length === 1 && !sending;

  return (
    <>
      {/* Floating launcher */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.94 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-32 right-4 md:bottom-6 md:right-6 z-[60] w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-emerald-500 text-white shadow-xl shadow-emerald-500/40 flex items-center justify-center"
            data-testid="support-chat-launcher"
            aria-label="Open support chat"
          >
            <span className="absolute inset-0 rounded-full bg-emerald-400/30 animate-ping opacity-40" style={{ animationDuration: '3s' }} />
            <Headset size={24} className="relative z-10" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <>
            {/* Dim backdrop (mobile bottom-sheet) */}
            <motion.div
              key="support-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[59] bg-slate-900/40 md:hidden"
              data-testid="support-chat-backdrop"
            />

            <motion.div
              key="support-panel"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              className={`fixed z-[60] bg-white shadow-2xl flex flex-col overflow-hidden transition-[height,bottom] duration-200 ease-out
                         left-0 right-0 rounded-t-[28px] ${IS_NATIVE ? '' : `bottom-0 ${focused ? 'h-[100dvh]' : 'h-[64vh]'} max-h-[100dvh]`}
                         md:inset-auto md:bottom-6 md:right-6 md:h-[600px] md:w-[400px] md:rounded-3xl md:max-h-none`}
              style={IS_NATIVE ? {
                bottom: 'var(--kb-height, 0px)',
                height: focused
                  ? 'calc(100dvh - var(--kb-height, 0px) - env(safe-area-inset-top) - 20px)'
                  : '64vh',
                maxHeight: '100dvh',
              } : undefined}
              data-testid="support-chat-panel"
            >
              {/* Header */}
              <div className="relative bg-gradient-to-br from-blue-600 to-emerald-500 px-5 pt-3 pb-4 overflow-hidden flex-shrink-0">
                <div className="absolute -top-8 -right-6 w-28 h-28 rounded-full bg-white/10" />
                {/* Grab handle (mobile) */}
                <div className="md:hidden relative w-10 h-1 rounded-full bg-white/40 mx-auto mb-3" />
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/30">
                      <Headset size={22} className="text-white" />
                    </div>
                    <div>
                      <p className="text-white font-extrabold text-[17px] leading-tight">GuestCheckout Assistant</p>
                      <p className="text-white/85 text-xs flex items-center gap-1.5 mt-0.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-200 inline-block animate-pulse" />
                        AI · online now
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setOpen(false)}
                    className="text-white/80 hover:text-white p-1.5 rounded-xl hover:bg-white/10 transition-colors"
                    data-testid="support-chat-close"
                    aria-label="Close support chat"
                  >
                    <X size={22} />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto bg-slate-50">
                <div className="px-4 py-4 space-y-3">
                  {messages.map((m, i) => {
                    const isGreeting = i === 0 && m.role === 'assistant';
                    if (isGreeting) {
                      return (
                        <p
                          key={i}
                          className="px-1 pt-1 text-slate-800 text-[15px] leading-relaxed whitespace-pre-wrap"
                          data-testid="support-msg-assistant"
                        >
                          {m.content}
                        </p>
                      );
                    }
                    return (
                      <div
                        key={i}
                        className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        data-testid={`support-msg-${m.role}`}
                      >
                        <div
                          className={`max-w-[82%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                            m.role === 'user'
                              ? 'bg-gradient-to-br from-blue-600 to-emerald-500 text-white rounded-2xl rounded-br-md'
                              : m.error
                              ? 'bg-rose-50 text-rose-700 border border-rose-100 rounded-2xl rounded-bl-md'
                              : 'bg-white text-slate-700 border border-slate-100 rounded-2xl rounded-bl-md shadow-sm'
                          }`}
                        >
                          {m.ticket && (
                            <span className="flex items-center gap-1.5 text-emerald-600 font-semibold text-xs mb-1">
                              <CheckCircle2 size={13} /> Ticket created
                            </span>
                          )}
                          {m.content}
                        </div>
                      </div>
                    );
                  })}

                  {/* Quick suggestions on first open — compact pills */}
                  {showSuggestions && (
                    <div className="flex flex-wrap gap-2 pt-1" data-testid="support-suggestions">
                      {SUGGESTIONS.map((s) => (
                        <button
                          key={s}
                          onClick={() => send(s)}
                          className="text-left text-xs font-semibold text-blue-700 bg-white border border-blue-100 hover:border-blue-300 hover:bg-blue-50 rounded-full px-3 py-1.5 transition-colors shadow-sm"
                          data-testid="support-suggestion"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}

                  {sending && (
                    <div className="flex justify-start" data-testid="support-typing">
                      <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-md shadow-sm px-4 py-3 flex items-center gap-1.5">
                        <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Input */}
              <div className="p-3 border-t border-slate-100 bg-white flex items-end gap-2 flex-shrink-0" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}>
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  rows={1}
                  placeholder="Ask me anything…"
                  className="flex-1 resize-none max-h-28 px-4 py-2.5 rounded-2xl bg-slate-100 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  data-testid="support-chat-input"
                />
                <button
                  onClick={() => send()}
                  disabled={!input.trim() || sending}
                  className="w-11 h-11 flex-shrink-0 rounded-2xl bg-gradient-to-br from-blue-600 to-emerald-500 text-white flex items-center justify-center shadow-md disabled:opacity-40 transition-opacity active:scale-95"
                  data-testid="support-chat-send"
                  aria-label="Send message"
                >
                  {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
